import { ListingType } from '@prisma/client';

interface TransactionParties {
  offer?: { userId?: string | null } | null;
  listings?: { userId?: string | null; type?: ListingType | null } | null;
}

/**
 * Who the buyer is on a transaction — the mirror of `resolveBuyerUserId` in
 * `apps/homepoint-api/src/services/inspection-summary/inspection-summary.helpers.ts`.
 *
 * `getUserRole` in the transaction dashboard answers a different question: it
 * keys off `listings.type` to decide which side's step components to render, so
 * it labels a buyer on a Homepoint-listed (seller-side) home as SELLER. That is
 * right for the step matrix and wrong for anything that has to follow the real
 * buyer, so buyer-facing surfaces resolve ownership the way the API does: the
 * accepted offer's owner is the buyer, falling back to the listing owner on a
 * buyer-side listing.
 */
export const resolveBuyerUserId = (
  transaction: TransactionParties | null | undefined
): string | null => {
  if (transaction?.offer?.userId) return transaction.offer.userId;
  if (
    transaction?.listings?.type === ListingType.BUYER &&
    transaction.listings.userId
  ) {
    return transaction.listings.userId;
  }
  return null;
};

/** Whether this user is the buyer on this transaction. */
export const isTransactionBuyer = (
  transaction: TransactionParties | null | undefined,
  userId: string | null | undefined
): boolean => Boolean(userId) && resolveBuyerUserId(transaction) === userId;

/**
 * Who the seller is on a transaction — the mirror of `resolveSellerUserId` in
 * `apps/homepoint-api/src/services/inspection-summary/inspection-summary.helpers.ts`.
 * Only a seller-side listing has a seller; a buyer-side listing has none.
 */
export const resolveSellerUserId = (
  transaction: TransactionParties | null | undefined
): string | null => {
  if (
    transaction?.listings?.type === ListingType.SELLER &&
    transaction.listings.userId
  ) {
    return transaction.listings.userId;
  }
  return null;
};

/** Whether this user is the seller on this transaction. */
export const isTransactionSeller = (
  transaction: TransactionParties | null | undefined,
  userId: string | null | undefined
): boolean => Boolean(userId) && resolveSellerUserId(transaction) === userId;
